import React, { useState, useEffect, useCallback, useRef } from "react";
import Modal from "../../../ui/Modal";

import styles from "./noticationsModal.module.css";
import { BsXLg } from "react-icons/bs";
import { useNavigate } from "react-router";
import CustomBottomScrollListener from "../../../utils/CustomBottomScrollListener";
import * as API from "../../../utils/api";
import Emitter from "../../../utils/event-emitter";

let isSmallScreen = window.innerWidth < 680 ? true : false;
const NotificationsModal = (props) => {
  const { isModalOpen, setIsModalOpen, user } = props;
  const navigate = useNavigate();
  const loadedPages = useRef(new Set());
  const loadedNotificationPages = useRef(new Set());

  const [isNotifications, setIsNotifications] = useState(false);

  const [activityPage, setActivityPage] = useState(0);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityHasMore, setActivityHasMore] = useState(true);
  const [recentEvents, setRecentEvents] = useState([]);
  const [notificationPage, setNotificationPage] = useState(0);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationHasMore, setNotificationHasMore] = useState(true);
  const [notificationsList, setNotificationsList] = useState([]);
  const [notificationsTotalCount, setNotificationsTotalCount] = useState(0);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [recentCount, setRecentCount] = useState(0);
  const prevNotificationsTab = useRef(undefined);
  const modalInitialLoadRef = useRef(false);

  const dashboardRecentActivities = useCallback(() => {
    if (
      activityLoading ||
      !activityHasMore ||
      loadedPages.current.has(activityPage)
    )
      return;
    setActivityLoading(true);
    loadedPages.current.add(activityPage);
    API.dashboardRecentActivities(user, activityPage, "All", [])
      .then((data) => {
        Emitter.emit("IS_QUICKBOOK_CONNECTED", data.qb_connected);
        if (typeof data != "undefined" || (data != null && !data.error)) {
          if (data.data?.length > 0) {
            let recent_activities = data.data;
            setActivityLoading(false);
            setActivityPage(data.pagination.page + 1);
            setActivityHasMore(recent_activities.length > 0 ? true : false);
            setRecentEvents((prevData) => [...prevData, ...data.data]);
            setRecentCount(data?.total_count || 0);
          }
        } else {
          setActivityHasMore(false);
        }
        setActivityLoading(false);
      })
      .catch((e) => {
        setActivityHasMore(false);
        setActivityLoading(false);
        console.log("error::", e.message);
      });
  }, [activityPage, activityLoading, activityHasMore, user]);

  const isNotificationApiSuccess = (data) =>
    data != null &&
    typeof data !== "undefined" &&
    !data.error &&
    (data.status_code === undefined || Number(data.status_code) === 200);

  const fetchNotificationHistory = useCallback(
    (requestedPage, { reset = false } = {}) => {
      const pageToFetch = reset
        ? 0
        : requestedPage !== undefined && requestedPage !== null
          ? requestedPage
          : notificationPage;

      if (!reset) {
        if (
          notificationLoading ||
          !notificationHasMore ||
          loadedNotificationPages.current.has(pageToFetch)
        ) {
          return Promise.resolve();
        }
      }

      if (reset) {
        loadedNotificationPages.current.clear();
        setNotificationHasMore(true);
      }

      setNotificationLoading(true);
      loadedNotificationPages.current.add(pageToFetch);

      return API.fetchNotificationHistory(user, pageToFetch, "unread")
        .then((data) => {
          if (!isNotificationApiSuccess(data)) {
            setNotificationHasMore(false);
            return;
          }

          const list = Array.isArray(data.notifications)
            ? data.notifications
            : [];
          const total =
            data.total_count != null ? Number(data.total_count) : list.length;
          setNotificationsTotalCount(Number.isFinite(total) ? total : 0);

          const mapped = list.map((item) => ({
            ...item,
            relative_time: formatRelativeTime(item.created_at),
            isVisible: false,
          }));

          const replaceList = reset || pageToFetch === 0;
          if (replaceList) {
            setNotificationsList(mapped);
          } else {
            setNotificationsList((prev) => [...prev, ...mapped]);
          }

          if (list.length === 0) {
            setNotificationHasMore(false);
          } else {
            setNotificationHasMore(true);
          }

          const nextPage = data.pagination?.page;
          if (nextPage !== undefined && nextPage !== null) {
            setNotificationPage(Number(nextPage) + 1);
          }
        })
        .catch((e) => {
          setNotificationHasMore(false);
          console.log("error::notifications", e.message);
        })
        .finally(() => {
          setNotificationLoading(false);
        });
    },
    [notificationPage, notificationLoading, notificationHasMore, user]
  );

  const fetchNotificationHistoryRef = useRef(fetchNotificationHistory);
  fetchNotificationHistoryRef.current = fetchNotificationHistory;

  // On open: fetch recent activities first, then notifications (badge counts + lists stay in sync).
  useEffect(() => {
    if (!isModalOpen || !user) return;

    modalInitialLoadRef.current = true;
    setIsNotifications(false);

    setActivityHasMore(true);
    setActivityLoading(true);
    setActivityPage(0);
    loadedPages.current = new Set([0]);
    setRecentEvents([]);
    setRecentCount(0);
    setNotificationPage(0);
    loadedNotificationPages.current = new Set();
    setNotificationsList([]);
    setNotificationsTotalCount(0);
    setNotificationHasMore(true);

    let cancelled = false;

    const load = async () => {
      try {
        const data = await API.dashboardRecentActivities(user, 0, "All", []);
        if (cancelled) return;
        Emitter.emit("IS_QUICKBOOK_CONNECTED", data.qb_connected);
        if (typeof data != "undefined" && data != null && !data.error) {
          if (data.data?.length > 0) {
            setRecentEvents(data.data);
            setActivityPage((data.pagination?.page ?? 0) + 1);
            setActivityHasMore(data.data.length > 0);
            setRecentCount(data?.total_count || 0);
          } else {
            setActivityHasMore(false);
            setRecentCount(data?.total_count || 0);
          }
        } else {
          setActivityHasMore(false);
        }
      } catch (e) {
        if (!cancelled) {
          setActivityHasMore(false);
          console.log("error::", e.message);
        }
      } finally {
        if (!cancelled) setActivityLoading(false);
      }

      if (cancelled) {
        modalInitialLoadRef.current = false;
        return;
      }
      try {
        await fetchNotificationHistoryRef.current(0, { reset: true });
      } finally {
        modalInitialLoadRef.current = false;
      }
    };

    load();

    return () => {
      cancelled = true;
      modalInitialLoadRef.current = false;
    };
  }, [isModalOpen, user]);

  useEffect(() => {
    if (!isModalOpen) {
      prevNotificationsTab.current = isNotifications;
      return;
    }
    if (isNotifications) {
      if (prevNotificationsTab.current === false) {
        fetchNotificationHistory(0, { reset: true });
      }
    } else if (
      prevNotificationsTab.current === true &&
      !modalInitialLoadRef.current
    ) {
      // User switched Notifications → Recent (not the forced tab on modal open).
      dashboardRecentActivities();
    }
    prevNotificationsTab.current = isNotifications;
  }, [
    isNotifications,
    isModalOpen,
    fetchNotificationHistory,
    dashboardRecentActivities,
  ]);

  const updateNotificationHistory = (event, id) => {
    event.stopPropagation();
    API.updateNotificationHistory(user, id)
      .then((data) => {
        fetchNotificationHistory(0, { reset: true });
      })
      .catch((e) => {
        console.log("err::", e.message);
      });
  };
  const onClose = () => {
    setIsModalOpen(false);
  };

  const handleActivityContainerOnBottom = () => {
    dashboardRecentActivities();
  };

  const handleNotificationContainerOnBottom = useCallback(() => {
    fetchNotificationHistory();
  }, [fetchNotificationHistory]);

  function formatRelativeTime(dateString) {
    const dateObj = new Date(dateString);
    const now = new Date();

    // Ensure accurate time difference calculation
    const diffInMilliseconds = now.getTime() - dateObj.getTime();
    const diffInSeconds = Math.floor(diffInMilliseconds / 1000);
    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
    const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
    const diffInWeeks = Math.floor(diffInDays / 7);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInSeconds < 60) return `${diffInSeconds} sec ago`;
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
    if (diffInDays === 1) return "1 day ago";
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInWeeks === 1) return "1 week ago";
    if (diffInDays < 30) return `${diffInWeeks} weeks ago`;
    if (diffInMonths === 1) return "1 month ago";
    if (diffInMonths < 12) return `${diffInMonths} months ago`;
    if (diffInYears === 1) return "1 year ago";
    return `${diffInYears} years ago`;
  }

  const getFormattedDate = (isoString) => {
    const dateObj = new Date(isoString);

    const date = dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    const time = dateObj.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      // second: "2-digit",
      hour12: true,
    });

    return `${date}`;
  };
  return (
    <div>
      <Modal
        isOpen={isModalOpen}
        onClose={onClose}
        header={
          <div className={styles.modalHeader}>
            <BsXLg
              onClick={() => {
                setIsModalOpen(false);
              }}
              className={styles.modalHeaderIcon}
            />
            <p className={styles.activityHeader}>Activity Feed</p>
          </div>
        }
        showCloseIcon={false}
        closeIconStyle={{ color: "black", height: "13.2px", width: "13.2px" }}
        style={{
          width: `${isSmallScreen ? "95%" : " 50%"}`,
          height: "100%",
        }}
        className={styles.modal}
        otherStyle={{ justifyContent: "flex-end" }}
      >
        <div>
          <div className={styles.buttonContainer}>
            <div
              className={`${!isNotifications ? styles.activeBorder : ""} ${styles.buttonContainerSub}`}
            >
              <button
                className={`${styles.tabButton} ${
                  !isNotifications ? styles.tabActive : ""
                }`}
                onClick={() => setIsNotifications(false)}
                // onClick={() => handleTabChange("activity")}
              >
                Recent Activities
              </button>
              <div className={styles.notificationCount}>{recentCount}</div>
            </div>
            <div
              className={`${isNotifications ? styles.activeBorder : ""} ${styles.buttonContainerSub}`}
            >
              <button
                className={`${styles.tabButton} ${
                  isNotifications ? styles.tabActive : ""
                }`}
                onClick={() => setIsNotifications(true)}
              >
                Notification
              </button>
              <div className={styles.notificationCount}>
                {notificationsTotalCount}
              </div>
            </div>
          </div>

          <div>
            {isNotifications ? (
              <>
                <CustomBottomScrollListener
                  offset={50}
                  onBottom={handleNotificationContainerOnBottom}
                  debounce={0}
                >
                  {(scrollRef) => (
                    <div
                      ref={scrollRef}
                      className={styles.upcomingAcrivityChipsWrapper}
                    >
                      {notificationsList.map((item, index) => {
                        const entityRef =
                          item?.mg_entity_number ||
                          item?.account_name ||
                          (item?.entity_type
                            ? String(item.entity_type).replace(/_/g, " ")
                            : null);
                        return (
                          <React.Fragment key={item.id ?? index}>
                            <div
                              className={styles.notificationchips}
                              onClick={() => {
                                localStorage.setItem("NOTIFICATION", true);
                                navigate(`/client/history`);
                                setIsModalOpen(false);
                              }}
                              onMouseOver={() => setHoveredItem(item.id)}
                              onMouseLeave={() => setHoveredItem(null)}
                            >
                              <div
                                className={styles.upcomingactivityContainer}
                              >
                                <div className={styles.activityCard}>
                                  <div className={styles.activityCardSub}>
                                    <div
                                      className={styles.upcomingactivityHeader}
                                    >
                                      <p className={styles.activityText}>
                                        {item?.error_message}
                                        {entityRef ? (
                                          <span className={styles.activityText}>
                                            {" "}
                                            #{entityRef}
                                          </span>
                                        ) : null}
                                      </p>
                                    </div>
                                    <div
                                      className={styles.activityTypeTextWrapper}
                                    >
                                      <p className={styles.activityTypeText}>
                                        {item.relative_time}
                                      </p>
                                    </div>
                                  </div>
                                  {isSmallScreen ? (
                                    <div className={styles.startTaskButton}>
                                      <BsXLg
                                        onClick={(event) => {
                                          updateNotificationHistory(
                                            event,
                                            item.id
                                          );
                                        }}
                                        className={styles.startTaskButtonIcon}
                                      />
                                    </div>
                                  ) : (
                                    <>
                                      {hoveredItem === item.id && (
                                        <div className={styles.startTaskButton}>
                                          <BsXLg
                                            onClick={(event) => {
                                              updateNotificationHistory(
                                                event,
                                                item.id
                                              );
                                            }}
                                            className={
                                              styles.startTaskButtonIcon
                                            }
                                          />
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}
                </CustomBottomScrollListener>
              </>
            ) : (
              <>
                <CustomBottomScrollListener
                  offset={50}
                  onBottom={handleActivityContainerOnBottom}
                  debounce={0}
                >
                  {(scrollRef) => (
                    <div
                      ref={scrollRef}
                      className={styles.upcomingAcrivityChipsWrapper}
                    >
                      {recentEvents.map((item, index) => {
                        return (
                          <>
                            <div className={styles.recentActivityChips}>
                              <div className={styles.upcomingactivityContainer}>
                                <div className={styles.activityCard}>
                                  <div>
                                    <div
                                      className={styles.upcomingactivityHeader}
                                    >
                                      <p className={styles.activityTypeText}>
                                        {item?.user_name}
                                      </p>
                                      {
                                        <div
                                          className={styles.activityContainer}
                                        >
                                          {item?.auto_send !== undefined &&
                                            item?.auto_send !== null && (
                                              <div
                                                className={styles.activityType}
                                              >
                                                <p
                                                  className={
                                                    styles.activityTypeText1
                                                  }
                                                >
                                                  {item?.auto_send
                                                    ? "Automated"
                                                    : "Manual"}
                                                </p>
                                              </div>
                                            )}
                                          <p
                                            className={styles.activityTypeText1}
                                          >
                                            {formatRelativeTime(item?.due_date)}
                                          </p>
                                        </div>
                                      }
                                    </div>
                                    <div>
                                      <p
                                        className={`${styles.activityText} ${styles.activityText2}`}
                                      >
                                        {getFormattedDate(item?.due_date)}{" "}
                                        {item?.count ? "-" + item?.text : ""}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        );
                      })}
                    </div>
                  )}
                </CustomBottomScrollListener>
              </>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};
export default NotificationsModal;
